/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Button, Col, message, Popconfirm, Row, Spin } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { LoadingOutlined } from '@ant-design/icons';

import Utils from '../../../Assets/Scripts/Utils';

import {
  getCustomization,
  getResourceCatalog,
  getRoleResourcePermissions,
  saveCustomization,
} from './API/FieldCustomizationApi';
import SectionsCustomizationPanel from './Components/SectionsCustomizationPanel';
import {
  applyRoleFieldPermissions,
  ensureExtraAttachmentsInCustomization,
  existingFieldOptions,
  isAgendaSchedulingResource,
  isExceptionTestField,
  removeSection,
  stripCustomizationCreatedFieldsFromSections,
  stripExceptionTestFields,
  stripExceptionTestNamesFromSections,
} from './fieldCustomizationRules';
import { reorderListItem, reorderSectionFieldNames } from './reorderCustomizationList';

import './FieldCustomizationStyle.scss';

const VISIBILITY_UNRESTRICTED = 'Unrestricted';
const VISIBILITY_RESTRICTED = 'Restricted';

function readCaughtErrorMessage(caught, fallback) {
  return caught?.errors?.[0]?.message || caught?.message || fallback;
}

function mergeOneCatalogField(catalog, overlayByName, index) {
  const overlay = overlayByName.get(catalog.name);
  if (overlay) {
    overlayByName.delete(catalog.name);
  }

  return {
    key: catalog.name,
    name: catalog.name,
    label: catalog.label,
    order: overlay?.order ?? index,
    visibilityMode: overlay?.visibilityMode || VISIBILITY_UNRESTRICTED,
    viewerRoleIds: overlay?.viewerRoleIds ?? [],
    isNew: Boolean(overlay?.isNew),
    fieldType: overlay?.fieldType || catalog.fieldType || null,
  };
}

function appendOverlayOnlyFields(merged, overlayByName) {
  overlayByName.forEach((overlay) => {
    merged.push({
      key: overlay.name,
      name: overlay.name,
      label: overlay.name,
      order: overlay.order,
      visibilityMode: overlay.visibilityMode || VISIBILITY_RESTRICTED,
      viewerRoleIds: overlay.viewerRoleIds ?? [],
      isNew: true,
      fieldType: overlay.fieldType,
    });
  });
}

function mergeCatalogWithOverlay(catalogFields, overlayFields) {
  const overlayByName = new Map((overlayFields ?? []).map((item) => [item.name, item]));
  const merged = (catalogFields ?? []).map((catalog, index) =>
    mergeOneCatalogField(catalog, overlayByName, index)
  );
  appendOverlayOnlyFields(merged, overlayByName);
  return merged.sort((left, right) => left.order - right.order);
}

function exceptionTestFieldNames(fields) {
  return (fields ?? [])
    .filter(isExceptionTestField)
    .map((field) => field.name ?? field.Name)
    .filter(Boolean);
}

function applyLoadStrips(mergedFields, overlaySections, resourceName) {
  const fields = stripExceptionTestFields(mergedFields, resourceName);
  const sectionsAfterException = isAgendaSchedulingResource(resourceName)
    ? stripExceptionTestNamesFromSections(overlaySections, exceptionTestFieldNames(mergedFields))
    : overlaySections;
  return {
    fields,
    sections: stripCustomizationCreatedFieldsFromSections(sectionsAfterException, fields),
  };
}

function sortSectionsByOrder(sections) {
  return [...(sections ?? [])].sort((left, right) => left.order - right.order);
}

function mountSaveField(field, order) {
  return {
    name: field.name,
    order,
    visibilityMode: VISIBILITY_UNRESTRICTED,
    viewerRoleIds: [],
  };
}

function parseTargetRoleId(fromRole) {
  if (fromRole == null || String(fromRole).trim() === '') {
    return null;
  }

  const parsed = Number(fromRole);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function mountSavePayload(resourceId, fields, sections, targetRoleId) {
  return {
    resourceId: Number(resourceId),
    roleId: targetRoleId,
    fields: fields
      .filter((field) => field.name)
      .map((field, index) => mountSaveField(field, index)),
    sections: sections.map((section, index) => ({
      sectionId: section.sectionId,
      name: section.name,
      order: index,
      fieldNames: section.fieldNames,
    })),
  };
}

function hasSectionWithoutName(sections) {
  return sections.some((section) => !(section.name ?? '').trim());
}

function exclusiveFieldNames(sections, sectionId, nextNames) {
  return sections.map((section) => {
    if (section.sectionId === sectionId) {
      return { ...section, fieldNames: nextNames };
    }

    return {
      ...section,
      fieldNames: section.fieldNames.filter((name) => !nextNames.includes(name)),
    };
  });
}

function CustomizationToolbar({ screenModified, loading, onBack, onSave }) {
  return (
    <div className="form-toolbar">
      {screenModified ? (
        <Popconfirm title="Deseja mesmo voltar?" onConfirm={onBack} okText="Sim" cancelText="Não">
          <Button>Voltar</Button>
        </Popconfirm>
      ) : (
        <Button onClick={onBack}>Voltar</Button>
      )}
      <Button loading={loading} type="primary" htmlType="button" onClick={onSave}>
        Gravar
      </Button>
    </div>
  );
}

function CustomizationLoading() {
  return (
    <Row gutter={[24]}>
      <Col span={24} className="field-customization-loading">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 56 }} spin />} />
      </Col>
    </Row>
  );
}

function MissingTargetRolePage({ screenModified, loading, onBack, onSave }) {
  return (
    <div className="field-customization-page">
      <CustomizationToolbar
        screenModified={screenModified}
        loading={loading}
        onBack={onBack}
        onSave={onSave}
      />
      <p>É preciso entrar pela edição do cargo.</p>
    </div>
  );
}

async function fetchCustomizationDraft(resourceId, targetRoleId) {
  const [resource, customization, permissions] = await Promise.all([
    getResourceCatalog(resourceId),
    getCustomization(resourceId, targetRoleId),
    getRoleResourcePermissions(targetRoleId, resourceId),
  ]);
  const catalogName = resource.name ?? '';
  const stripped = applyLoadStrips(
    mergeCatalogWithOverlay(resource.fields, customization.fields),
    sortSectionsByOrder(customization.sections),
    catalogName
  );
  const permitted = applyRoleFieldPermissions(stripped.fields, stripped.sections, permissions);
  const ensured = ensureExtraAttachmentsInCustomization(permitted.fields, permitted.sections);
  return { catalogName, fields: ensured.fields, sections: ensured.sections };
}

function FieldCustomizationView() {
  const { resourceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromRole = searchParams.get('fromRole');
  const targetRoleId = parseTargetRoleId(fromRole);

  const [loading, setLoading] = useState(false);
  const [screenModified, setScreenModified] = useState(false);
  const [resourceName, setResourceName] = useState('');
  const [fields, setFields] = useState([]);
  const [sections, setSections] = useState([]);

  const markModified = (nextFields, nextSections) => {
    if (nextFields) setFields(nextFields);
    if (nextSections) setSections(nextSections);
    setScreenModified(true);
  };

  const loadScreen = async () => {
    if (!targetRoleId) {
      return;
    }

    setLoading(true);
    try {
      const draft = await fetchCustomizationDraft(resourceId, targetRoleId);
      setResourceName(draft.catalogName);
      setFields(draft.fields);
      setSections(draft.sections);
      setScreenModified(false);
    } catch (caught) {
      Utils.logError(caught);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadScreen();
  }, [resourceId, targetRoleId]);

  const goBack = () => {
    if (fromRole) {
      navigate(`/Cargo/Editar/${fromRole}`);
      return;
    }

    navigate(-1);
  };

  const handleCreateSection = (name) => {
    const trimmed = (name ?? '').trim();
    if (!trimmed) {
      message.error('Nome da seção é obrigatório.');
      return false;
    }

    markModified(undefined, [
      ...sections,
      { sectionId: Utils.generateUUID(), name: trimmed, order: sections.length, fieldNames: [] },
    ]);
    return true;
  };

  const handleDeleteSection = (sectionId) => {
    markModified(undefined, removeSection(sections, sectionId));
  };

  const handleSave = async () => {
    if (!targetRoleId) {
      return;
    }

    if (hasSectionWithoutName(sections)) {
      message.error('Nome da seção é obrigatório.');
      return;
    }

    setLoading(true);
    try {
      await saveCustomization(mountSavePayload(resourceId, fields, sections, targetRoleId));
      message.success('Customização gravada!');
      setScreenModified(false);
      await loadScreen();
    } catch (caught) {
      Utils.logError(caught);
      message.error(readCaughtErrorMessage(caught, 'Oops. Erro ao gravar a customização!'));
    }
    setLoading(false);
  };

  if (!targetRoleId) {
    return (
      <MissingTargetRolePage
        screenModified={screenModified}
        loading={loading}
        onBack={goBack}
        onSave={handleSave}
      />
    );
  }

  if (loading && fields.length === 0) {
    return <CustomizationLoading />;
  }

  const labelByName = Object.fromEntries(fields.map((field) => [field.name, field.label]));
  const fieldOptions = existingFieldOptions(fields);

  return (
    <div className="field-customization-page">
      <CustomizationToolbar
        screenModified={screenModified}
        loading={loading}
        onBack={goBack}
        onSave={handleSave}
      />
      <h2>Customização{resourceName ? ` — ${resourceName}` : ''}</h2>
      <SectionsCustomizationPanel
        sections={sections}
        fieldOptions={fieldOptions}
        labelByName={labelByName}
        onCreateSection={handleCreateSection}
        onReorderSection={(fromIndex, toIndex) => {
          const next = reorderListItem(sections, fromIndex, toIndex);
          if (next !== sections) markModified(undefined, next);
        }}
        onRenameSection={(sectionId, name) =>
          markModified(
            undefined,
            sections.map((section) =>
              section.sectionId === sectionId ? { ...section, name } : section
            )
          )
        }
        onSectionFieldsChange={(sectionId, fieldNames) =>
          markModified(undefined, exclusiveFieldNames(sections, sectionId, fieldNames))
        }
        onReorderSectionField={(sectionId, fromIndex, toIndex) => {
          const next = reorderSectionFieldNames(sections, sectionId, fromIndex, toIndex);
          if (next !== sections) markModified(undefined, next);
        }}
        onDeleteSection={handleDeleteSection}
      />
    </div>
  );
}

export default FieldCustomizationView;
